/**
 * Home / Command Center (P26 + P30 strip).
 *
 * V1 strip: only renders data the control-plane actually returns. Wireframe
 * decoration (role-command-brief, hero gradient, operational-states cards,
 * integration-readiness table) was wireframe scaffolding with no backend
 * — removed so the dashboard reflects the real product surface.
 */
import type { JSX } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  Boxes,
  Fingerprint,
  Plus,
  Route as RouteIcon,
  ShieldCheck,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { useDashboardSummary } from '@/api/index.js';
import { ActionButton } from '@/components/console/ActionButton.js';
import { MetricCard } from '@/components/console/MetricCard.js';
import { PageHeader } from '@/components/console/PageHeader.js';
import { AsyncShell } from '@/components/console/StateBlocks.js';

export default function Home(): JSX.Element {
  const summary = useDashboardSummary();
  const live = summary.data;

  const denyRate =
    live && live.audit_24h.allow + live.audit_24h.deny > 0
      ? ((live.audit_24h.deny * 100) / (live.audit_24h.allow + live.audit_24h.deny)).toFixed(2) +
        '%'
      : '—';
  const totalDecisions24h = live
    ? live.audit_24h.allow + live.audit_24h.deny + live.audit_24h.error
    : 0;

  return (
    <>
      <PageHeader
        eyebrow="Stanford MCP Control Plane"
        title="Command Center"
        description="Live operational view: registered servers, active tools, pending approvals, and audit-event totals over the last 24h."
      >
        <Link to="/govern/audit">
          <ActionButton icon={Fingerprint}>Inspect audit trail</ActionButton>
        </Link>
        <Link to="/build/register">
          <ActionButton icon={Plus} primary>
            Register MCP server
          </ActionButton>
        </Link>
      </PageHeader>

      <AsyncShell
        isLoading={summary.isLoading}
        isError={summary.isError}
        isEmpty={!live}
        error={summary.error}
        loadingTitle="Loading dashboard summary"
        errorTitle="Could not reach the gateway API"
        errorHint="Hard-refresh the page (Ctrl+Shift+R). If it persists, sumcp-control-plane on the gateway box may be down."
      >
        {live && (
          <>
            <div className="metric-grid">
              <MetricCard
                icon={Boxes}
                label="Active servers"
                value={String(live.servers.active)}
                delta={`${live.servers.total - live.servers.active} non-active in registry`}
                tone="good"
                spark={[0, 0, 0, 0, 0, live.servers.active]}
              />
              <MetricCard
                icon={RouteIcon}
                label="Active tools"
                value={String(live.tools.active)}
                delta={`${live.tools.total} registered overall`}
                tone="good"
                spark={[0, 0, 0, 0, 0, live.tools.active]}
              />
              <MetricCard
                icon={ShieldCheck}
                label="Decisions (24h)"
                value={totalDecisions24h.toLocaleString()}
                delta={`${denyRate} denied · ${live.audit_total_all_time.toLocaleString()} all-time`}
                tone="neutral"
                spark={[0, 0, 0, 0, 0, totalDecisions24h]}
              />
              <MetricCard
                icon={AlertTriangle}
                label="Pending approvals"
                value={String(live.approvals.pending)}
                delta={`${live.audit_24h.error} errors in last 24h`}
                tone={live.approvals.pending > 0 ? 'warn' : 'good'}
                spark={[0, 0, 0, 0, 0, live.approvals.pending]}
              />
            </div>

            <p className="subtle" style={{ fontSize: 14, marginTop: 8 }}>
              Generated at {new Date(live.generated_at).toLocaleTimeString()}.
            </p>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: 12,
                marginTop: 24,
              }}
            >
              <Link to="/catalog" className="quick-link">
                <ArrowRight size={14} /> Browse {live.servers.active} active server
                {live.servers.active === 1 ? '' : 's'} in the catalog
              </Link>
              <Link to="/govern/approvals" className="quick-link">
                <ArrowRight size={14} /> Review {live.approvals.pending} pending approval
                {live.approvals.pending === 1 ? '' : 's'}
              </Link>
              <Link to="/build/publisher" className="quick-link">
                <ArrowRight size={14} /> Manage {live.servers.total} registered server
                {live.servers.total === 1 ? '' : 's'}
              </Link>
              <Link to="/govern/audit" className="quick-link">
                <ArrowRight size={14} /> Inspect {live.audit_total_all_time.toLocaleString()} audit
                event
                {live.audit_total_all_time === 1 ? '' : 's'}
              </Link>
            </div>
          </>
        )}
      </AsyncShell>
    </>
  );
}
